import React, { useEffect, useRef, useState } from 'react';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import {
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { DeleteTestUserDialog } from '../../libs/test-users/DeleteTestUserDialog';
import { Page, PageBody, PageHeader } from '../../libs/shared/page';
import { PaginationFooter } from '../../libs/shared/pagination-footer/PaginationFooter';
import { ProfileDisplayName } from '../../libs/profiles/utils/ProfileDisplayName';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { QlfPageErrorBoundary } from '../../libs/shared/QlfPageErrorBoundary';
import { TestUserDetails } from '../../libs/test-users/TestUserDetails';
import { TopBar } from '../../libs/shared/layout/TopBar';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useQueryParams } from '../../libs/shared/use-query-params';
import { useTestUsers } from '../../libs/test-users/TestUsersContext';

const useStyles = makeStyles({
  sidebarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '10px',
  },
});

function Root() {
  const { t } = useTranslation();
  const { current: search$ } = useRef(new Subject<string>());
  const {
    testUsers,
    fetchTestUsers,
    selectedTestUser,
    setSelectedTestUser,
    setCreationPopupIsOpen,
    currentPage,
    setCurrentPage,
    goToNextPage,
    goToPreviousPage,
    isLoading,
  } = useTestUsers();
  const { features } = useFeatures();
  const testUsersWrite = features[Feature.TEST_USERS].write;
  const testUsersRemove = features[Feature.TEST_USERS].remove;

  useEffect(() => {
    const subscription = search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a === b),
      )
      .subscribe(value => fetchTestUsers({ search: value, pageNumber: currentPage }));
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetchTestUsers();
    return () => {
      setSelectedTestUser(undefined);
    };
  }, [fetchTestUsers]);

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('test-users.sidebar.search')}
      onChange={e => search$.next(e.target.value)}
      onAdd={() => {
        setCreationPopupIsOpen(true);
        setCurrentPage(0);
      }}
      disableAdd={!testUsersWrite}
      onAddTooltip={testUsersWrite ? '' : t('shared.action.permission.required')}
    />
  );

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<ProfileAttributes | undefined>();
  const { setParams } = useQueryParams();

  const templateRow = (props: { data: ProfileAttributes }) => {
    const { data: profile } = props;
    const options = [
      {
        node: <p>{t('generic.actions.hard-delete')}</p>,
        startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
        callback: () => {
          setProfileToDelete(profile);
          setIsDeletePopupOpen(true);
        },
        disabled: !testUsersRemove,
        tooltip: testUsersRemove ? '' : t('shared.action.permission.required'),
      },
    ];
    return (
      <QlfSimpleDetail
        state={profile.id === selectedTestUser?.id ? 'active' : 'default'}
        options={options}
        callback={() => {
          setSelectedTestUser(props.data);
          setParams({ profileId: profile.id });
        }}
        content={<ProfileDisplayName profile={profile} />}
      />
    );
  };

  const changePage = (step: number) => {
    if (step > 0) {
      goToNextPage();
    } else {
      goToPreviousPage();
    }
  };

  const templateFooter = (
    <PaginationFooter
      changePage={changePage}
      currentPage={currentPage}
      dataPage={testUsers}
      isLoading={isLoading('searchProfiles')}
    />
  );

  return (
    <Page>
      <PageHeader>
        <TopBar />
      </PageHeader>
      <PageBody>
        <DeleteTestUserDialog
          isOpen={isDeletePopupOpen}
          setIsOpen={setIsDeletePopupOpen}
          profile={profileToDelete}
          onDeleted={() => {
            setProfileToDelete(undefined);
          }}
        />
        <QlfPageErrorBoundary>
          <QlfMasterDetailContainer isAccountLevel>
            <QlfMasterDetail
              templateHeader={templateHeader}
              templateRow={templateRow}
              templateDetail={TestUserDetails}
              templateFooter={templateFooter}
              items={testUsers?.data || []}
            />
          </QlfMasterDetailContainer>
        </QlfPageErrorBoundary>
      </PageBody>
    </Page>
  );
}

export function TestUsersIndexPage() {
  return <Root />;
}
